/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { mergeObjectsByEndTime } from "~/helpers/utils";
import { eventServices, subscriberService } from "~/services";
import { getThirdPartyCBRS, getThirdPartyPublicCellTowers } from "~/services/radio-service";
import { EventSummary, EventsSummaryQuery } from "~/types/event";
import { updateURL } from '~/helpers/history';
import { MetricsQuery } from "~/services/APIServices";
import { fillTimeSeriesData } from "~/views/subscribers/graphs/graphHelper";
import { Label } from "~/types/metrics";
import { metricHelper } from "~/helpers/metricHelper";
import { AppInsightsMetrics, AppInsightsMetricsMap, AppInsightsMetricsSumMap } from "~/views/ITDashboard/AppInsights/InsightsMetricList";
import { ThirdPartyCBRS, ThirdPartyPublicCellTower, ThirdPartyRequest } from "~/types/ThirdParty";
import Radio from "~/types/radio";
interface timelineData {
  timestamp: string;
  bytes: number;
  rate: number;
  event?: number;
}

interface SelectedPanelProps {
  type: string;
  nodeID: string;
}

interface Site {
  id : string,
  name : string,
  location : {
    latitude : number,
    longitude : number,
  },
}

export enum TabTypes {
  networkMap = 'networkMap',
  events = 'events',
  insights = 'insights',
  dailyReport = 'dailyReport',
  appInsights = 'appInsights',
  planningMap = 'planningMap',
}

type initState = {
  timelineData: timelineData[];
  selectedPanel: SelectedPanelProps;
  leafNodeID: string;
  selectedTimestamp: number;
  selectedEventTimestamp: number;
  loading: boolean;
  eventData: EventSummary[];
  vendorType: string;
  activeTab: TabTypes;
  lastUpdatedAt: number;
  appInsights: {
    data: Record<string, {
      key: string;
      labels: Label[];
      data: [number, any][];
    }[]>;
    loading: boolean;
    sumData: Record<string, {
      key: string;
      labels: Label[];
      data: any;
    }[]>;
  },

  planningMap: {
    data : {
      cbrs : ThirdPartyCBRS[],
      public : ThirdPartyPublicCellTower[],
      highway9 : Radio[],
    },
    site : Site[] | null,
    loading : boolean,
    source : string[],
    technology : string[],
    zoneType : string[],
    radius : string,
  }
};

// Cache for the planning map based on the site and radius
const planningMapCache = new Map<string, {
  cbrs : ThirdPartyCBRS[],
  public : ThirdPartyPublicCellTower[],
}>();

// Bound the planning-map cache (FIFO) so it can't grow without limit over a session (MCS-3969).
const PLANNING_MAP_CACHE_MAX = 300;
function setPlanningMapCache(key: string, value: { cbrs: ThirdPartyCBRS[]; public: ThirdPartyPublicCellTower[] }) {
  planningMapCache.set(key, value);
  while (planningMapCache.size > PLANNING_MAP_CACHE_MAX) {
    const oldestKey = planningMapCache.keys().next().value;
    if (oldestKey === undefined) break;
    planningMapCache.delete(oldestKey);
  }
}

export const initialState: initState = {
  timelineData: [],
  selectedPanel: {
    type: "",
    nodeID: "",
  },
  leafNodeID: "",
  selectedTimestamp: 0,
  selectedEventTimestamp: 0,
  loading: true,
  eventData: [],
  vendorType: "",
  activeTab: TabTypes.networkMap,
  lastUpdatedAt: 0,
  appInsights: {
    sumData : {},
    data: {},
    loading: true,
  },
  planningMap: {
    data : {
      cbrs : [],
      public : [],
      highway9 : [],
    },
    site : null,
    loading : true,
    source : ['highway9', 'cbrs', 'public'],
    technology : ['5G', '4G'],
    zoneType : ['indoor', 'outdoor'],
    radius : '1000',
  }
};


export const fetchPlanningMapRadioData = createAsyncThunk(
  "itDashboard/fetchPlanningMapRadioData",
  async (_, thunk) => {
    const state = thunk.getState() as RootState;
    const radios = state.radio.completeData
    return radios;
  }
)

export const fetchPlanningMapCBRSAndPublicData = createAsyncThunk(
  "itDashboard/fetchPlanningMapCBRSAndPublicData",
  async (_, thunk) => {
    const state = thunk.getState() as RootState;
    // Get Selected Sites 
    const sites = state.itDashboard.planningMap.site;
    const radius = Number(state.itDashboard.planningMap.radius);

    // MCS-3778: KPI-created sites have no location; skip them to avoid undefined-property crashes
    // and to avoid sending bogus (0,0) coordinates upstream.
    const requests = sites?.filter((site) => site.location?.latitude && site.location?.longitude).map((site) => {
      return {
        radius: radius,
        centerLocation: {
          latitude: site.location!.latitude,
          longitude: site.location!.longitude,
        }
      } satisfies ThirdPartyRequest
    }) ?? []

    const cacheKeys = sites?.map((site) => `${radius}-${site.id}`) ?? [];
    const cache = cacheKeys.map((key) => planningMapCache.get(key));

    // Check if all data is already cached
    const allCached = cache.every((cacheData) => cacheData !== undefined);
    
    if (allCached) {
      // Return cached data
      const cachedCbrs = cache.flatMap((cacheData) => cacheData?.cbrs || []);
      const cachedPublic = cache.flatMap((cacheData) => cacheData?.public || []);
      
      return {
        cbrs: cachedCbrs,
        public: cachedPublic,
      };
    }

    // Fetch data for sites that are not cached
    const uncachedRequests: { request: ThirdPartyRequest; cacheKey: string }[] = [];
    
    requests.forEach((request, index) => {
      const cacheKey = cacheKeys[index];
      if (!planningMapCache.get(cacheKey)) {
        uncachedRequests.push({ request, cacheKey });
      }
    });

    const cbrsRequests = uncachedRequests.map(({ request }) => getThirdPartyCBRS(request));
    const publicRequests = uncachedRequests.map(({ request }) => getThirdPartyPublicCellTowers(request));
    
    const [cbrsData, publicData] = await Promise.allSettled([Promise.all(cbrsRequests), Promise.all(publicRequests)]);

    const _cbrs = cbrsData.status === 'fulfilled' ? cbrsData.value : [];
    const _public = publicData.status === 'fulfilled' ? publicData.value : [];

    // Cache the new data
    uncachedRequests.forEach(({ cacheKey }, index) => {
      const cbrsForSite = _cbrs[index] || [];
      const publicForSite = _public[index] || [];
      
      setPlanningMapCache(cacheKey, {
        cbrs: cbrsForSite,
        public: publicForSite,
      });
    });

    // Combine cached and new data
    const allCbrs = cache.flatMap((cacheData) => cacheData?.cbrs || []).concat(_cbrs.flat());
    const allPublic = cache.flatMap((cacheData) => cacheData?.public || []).concat(_public.flat());

    return {
      cbrs: allCbrs,
      public: allPublic,
    };
  }
)

export const fetchEvents = createAsyncThunk(
  "itDashboard/fetchEvents",
  async ({ startTime, endTime, interval }: { startTime: number; endTime: number; interval: number }) => {
    const payload: EventsSummaryQuery = {
      category: "FAILURE",
      objectType: "ALL",
      interval: {
        startTime,
        endTime,
      },
      aggregationInterval: Math.round(interval),
    }
    const response = await eventServices.getEventSummary(payload);
    const mergedData = mergeObjectsByEndTime(response);
    return mergedData;
  }
);

export const fetchAppInsights = createAsyncThunk(
  "itDashboard/fetchAppInsights",
  async (tab : string, thunk) => {
    const state = thunk.getState() as RootState;
    const { startTime, endTime } = state.utility.time;
    // round the resolution to the nearest 60 seconds
    let resolution = (Math.max(metricHelper.getResolution(startTime, endTime, 200), 60))
    resolution = Math.round(resolution / 60) * 60;
    // We need to create a list of queries for each metric where labels are different for each metric
    // For example, for the first metric, the labels are [ 'application', 'application_category' ]
    // For the second metric, the labels are [ 'application', 'application_list' ]
    // For the third metric, the labels are [ 'application', 'application_category' ]
    // the the first and third metric have the same labels, so we can combine them into one query with the same labels and 2nd metric will be a separate query with different labels 
   
    const createQuery = (metric : AppInsightsMetrics) => ({
      metrics: metric.metricKeys instanceof Array ? metric.metricKeys : [metric.metricKeys],
      interval: { startTime, endTime },
      labels: metric.labels,
      resolution: resolution,
      ids: ["ALL"],
    } satisfies MetricsQuery);

    const queries = (AppInsightsMetricsMap[tab]).reduce((acc, metric, i) => {
      // If it's the first metric or the labels are different from the previous metric, create a new query
      if (i === 0 || metric.labels.join() !== acc[acc.length - 1].labels?.join()) {
        acc.push(createQuery(metric));
      } else {
        // If the labels are the same as the previous metric, add the metric to the previous query
        const metricKey = metric.metricKeys instanceof Array ? metric.metricKeys : [metric.metricKeys];
        
        // Use a Set to avoid duplicate metric keys
        const uniqueMetrics = new Set(acc[acc.length - 1].metrics);
        metricKey.forEach(key => uniqueMetrics.add(key));
        
        // Convert the Set back to an array
        acc[acc.length - 1].metrics = Array.from(uniqueMetrics);
      }
    
      return acc;
    }, [] as MetricsQuery[]);

    const promises = queries.map((query) => subscriberService.getMetrics(query));

    const response = (await Promise.all(promises)).flat();
    return {
      data: response,
      interval: { startTime, endTime },
      resolution
    }
  }
);

export const fetchAppInsightsSum = createAsyncThunk(
  "itDashboard/fetchAppInsightsSum", async (tab : string, thunk) => {
    const state = thunk.getState() as RootState;
    const { startTime, endTime } = state.utility.time;
    const resolution = endTime - startTime;
    // We need to create a list of queries for each metric where labels are different for each metric
    // For example, for the first metric, the labels are [ 'application', 'application_category' ]
    // For the second metric, the labels are [ 'application', 'application_list' ]
    // For the third metric, the labels are [ 'application', 'application_category' ]
    // the the first and third metric have the same labels, so we can combine them into one query with the same labels and 2nd metric will be a separate query with different labels 
    const createQuery = (metric : AppInsightsMetrics) => ({
      metrics: metric.metricKeys instanceof Array ? metric.metricKeys : [metric.metricKeys],
      interval: { startTime, endTime },
      labels: metric.labels,
      resolution: resolution,
      ids: ["ALL"],
    } satisfies MetricsQuery);

    const queries = AppInsightsMetricsSumMap[tab].reduce((acc, metric, i) => {
      // If it's the first metric or the labels are different from the previous metric, create a new query
      if (i === 0 || metric.labels.join() !== acc[acc.length - 1].labels?.join()) {
        acc.push(createQuery(metric));
      } else {
        // If the labels are the same as the previous metric, add the metric to the previous query
        const metricKey = metric.metricKeys instanceof Array ? metric.metricKeys : [metric.metricKeys];
        acc[acc.length - 1].metrics.push(...metricKey);
      }
      return acc;
    }, [] as MetricsQuery[]);
    const promises = queries.map((query) => subscriberService.getMetrics(query));
    const response = (await Promise.all(promises)).flat();
    return {
      data: response,
      interval: { startTime, endTime },
      resolution
    }
  }
);

const itDashboardSlice = createSlice({
  name: "itDashboard",
  initialState,
  reducers: {
    setTimelineData: (state, action) => {
      state.timelineData = action.payload;
    },
    setSelectedPanel: (state, action) => {
      state.selectedPanel = action.payload;
    },
    setLeafNodeID: (state, action) => {
      state.leafNodeID = action.payload;
    },
    setSelectedTimestamp: (state, action) => {
      state.selectedTimestamp = action.payload;
      updateURL({ timestamp: action.payload });
    },
    setSelectedEventTimestamp: (state, action) => {
      state.selectedEventTimestamp = action.payload;
    },
    setVendorType: (state, action) => {
      state.vendorType = action.payload;
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      updateURL({ tab: action.payload });
    },

    // Planning Map Tab
    setPlanningMapSource: (state, action : PayloadAction<string[]>) => {
      state.planningMap.source = action.payload;
    },
    setPlanningMapTechnology: (state, action : PayloadAction<string[]>) => {
      state.planningMap.technology = action.payload;
    },
    setPlanningMapZoneType: (state, action : PayloadAction<string[]>) => {
      state.planningMap.zoneType = action.payload;
    },
    setPlanningMapRadius: (state, action : PayloadAction<string>) => {
      state.planningMap.radius = action.payload;
    },
    setPlanningMapSite: (state, action : PayloadAction<Site[]>) => {
      // at-least 1 site is required to show the map
      if(action.payload.length > 0) {
        state.planningMap.site = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventData = action.payload;
        state.loading = false;
        state.lastUpdatedAt = Date.now();
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.eventData = [];
        state.lastUpdatedAt = Date.now();
        console.error(action.error);
      })

      .addCase(fetchAppInsights.pending, (state) => {
        state.appInsights.loading = true;
      })
      .addCase(fetchAppInsights.fulfilled, (state, action) => {
        const { data, interval, resolution } = action.payload;

        data.forEach((d) => {
          const key = d.metric;
          const _data = d.metricData ?? [];
          const xData = _data.map((metric) => {
            const { dataPoints, labels } = metric;
            const tempKey = key + "#" + labels?.map((label) => `${label.name}:${label.value}`).join("&");

            if (dataPoints?.length === 0) {
              return {
                key: tempKey,
                labels,
                data: [],
              }
            }
            try {
              const updatedData = fillTimeSeriesData({
                data: dataPoints,
                interval: resolution,
                startTime: interval.startTime,
                endTime: interval.endTime,
                fillType: "none",
                timeRoundingToNearest: "minutes",
                name: key,
              })
              return {
                key: tempKey,
                labels,
                data: updatedData,
              }
            }
            catch (e) {
              console.error("Error in fillTimeSeries", e, key, metric.dataPoints);
              return {
                key: tempKey,
                labels,
                data: metric.dataPoints.length ? metric.dataPoints : []
              }
            }
          })

          state.appInsights.data[key] = xData;
        });

        state.appInsights.loading = false;
      })
      .addCase(fetchAppInsights.rejected, (state, action) => {
        state.appInsights.loading = false;
        console.error(action.error);
      })

      .addCase(fetchAppInsightsSum.pending, (state) => {
        state.appInsights.loading = true;
      })
      .addCase(fetchAppInsightsSum.fulfilled, (state, action) => {
        const { data } = action.payload;

        data.forEach((d) => {
          const key = d.metric;
          const _data = d.metricData ?? [];
          const xData = _data.map((metric) => {
            const { dataPoints, labels } = metric;
            let tempKey = key;
            if(labels?.length > 0) tempKey += "#" + labels?.map((label) => `${label.name}:${label.value}`).join("&");

           return {
              key: tempKey,
              labels,
              data: dataPoints[dataPoints.length - 1][1],
            }
          })
          state.appInsights.sumData[key] = xData;
        });
      })

      .addCase(fetchPlanningMapRadioData.fulfilled, (state, action) => {
        state.planningMap.data.highway9 = action.payload;
        state.planningMap.loading = false;
      })
      .addCase(fetchPlanningMapCBRSAndPublicData.fulfilled, (state, action) => {
        state.planningMap.data.cbrs = action.payload.cbrs;
        state.planningMap.data.public = action.payload.public;
        state.planningMap.loading = false;
      })
  },
});

export const itDashboardAction = itDashboardSlice.actions;
export default itDashboardSlice.reducer;

export const timelineData = (state: RootState) => state.itDashboard.timelineData;
export const selectedPanel = (state: RootState) => state.itDashboard.selectedPanel;
export const selectedLeafNodeID = (state: RootState) => state.itDashboard.leafNodeID;
export const selectedTimestamp = (state: RootState) => state.itDashboard.selectedTimestamp;
export const selectedEventTimestamp = (state: RootState) => state.itDashboard.selectedEventTimestamp;
export const eventData = (state: RootState) => state.itDashboard.eventData;
export const loading = (state: RootState) => state.itDashboard.loading;
export const vendorType = (state: RootState) => state.itDashboard.vendorType;
export const activeTab = (state: RootState) => state.itDashboard.activeTab;
export const lastUpdatedAt = (state: RootState) => state.itDashboard.lastUpdatedAt;


// type DataT = {
//   startTime: number;
//   endTime: number;
//   interval: number;
//   range : [number, number];
// }

// function generateData({startTime, endTime, interval, range}: DataT) {
//   const data: [number, number][] = [];

//   // Generate random data for smooth curve
//   let prevValue = Math.random() * (range[1] - range[0]) + range[0];
//   for (let i = startTime; i <= endTime; i += interval) {
//     const value = prevValue + (Math.random() - 0.2) * 10;
//     data.push([i * 1000, Number(value.toFixed(1))]);
//     prevValue = value;
//   }

  
  
//   return data;
// }